import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, HelpCircle, Calendar, Users, TrendingUp,
  Search, AlertTriangle
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend } from 'recharts';
import { formatDateAU } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `$${Math.round(v).toLocaleString()}`;

const RevenueTimeline = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/revenue-security`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue security data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-12 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[#E5E5E5]" />
        <p className="text-sm text-[#525252]">No data available. Upload a Master List with management expiry dates.</p>
      </div>
    );
  }

  const { summary, quarterlyTimeline, securityOverTime, managerBreakdown, plans } = data;
  const hasExpiryData = summary.securedPlans > 0 || summary.unsecuredPlans > 0;

  const filteredPlans = plans.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.planNumber.toLowerCase().includes(term)
      || p.propertyName?.toLowerCase().includes(term)
      || p.manager?.toLowerCase().includes(term);
  });

  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-3 shadow-lg text-sm">
          <p className="font-semibold text-[#171717]">{label}</p>
          <p className="text-emerald-600">{fmt(payload[0].value)} revenue</p>
          {payload[0].payload.count > 0 && (
            <p className="text-[#737373] text-xs">{payload[0].payload.count} plan(s) expiring</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="revenue-timeline-view">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Revenue Timeline</h1>
        <p className="text-sm text-[#737373] mt-1">Contract renewal pipeline and secured revenue forecasting.</p>
      </div>

      {/* No expiry data notice */}
      {!hasExpiryData && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-[10px] border bg-amber-50 border-amber-200 text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
          <span className="text-sm font-medium">
            No contract expiry dates found in your Master List. Re-upload your Master List with a "Management Expiry Date" column to enable revenue forecasting.
          </span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            <span className="text-xs font-medium text-[#737373] uppercase tracking-wider">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-[#171717]">{fmt(summary.totalRevenue)}</p>
          <p className="text-xs text-[#A3A3A3] mt-1">{summary.totalPlans} plans</p>
        </div>

        <div className="bg-white border border-emerald-200 rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />
            <span className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Secured</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{fmt(summary.securedRevenue)}</p>
          <p className="text-xs text-emerald-500 mt-1">{summary.securedPlans} plans ({summary.securedPct}%)</p>
        </div>

        <div className="bg-white border border-red-200 rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-red-500" strokeWidth={1.5} />
            <span className="text-xs font-medium text-red-600 uppercase tracking-wider">Unsecured</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{fmt(summary.unsecuredRevenue)}</p>
          <p className="text-xs text-red-500 mt-1">{summary.unsecuredPlans} plans ({summary.unsecuredPct}%)</p>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
            <span className="text-xs font-medium text-[#737373] uppercase tracking-wider">No Agreement Date</span>
          </div>
          <p className="text-2xl font-bold text-[#525252]">{fmt(summary.noDateRevenue)}</p>
          <p className="text-xs text-[#A3A3A3] mt-1">{summary.noDatePlans} plans ({summary.noDatePct}%)</p>
        </div>
      </div>

      {/* Secured vs Unsecured Over Time — Revenue */}
      {securityOverTime && securityOverTime.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <div className="px-6 py-4 border-b border-[#E5E5E5]">
              <h2 className="text-base font-semibold text-[#171717] font-heading">Revenue Security Over Time</h2>
              <p className="text-xs text-[#737373]">How secured revenue decreases as contracts expire each quarter</p>
            </div>
            <div className="p-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={securityOverTime} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#737373' }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-3 shadow-lg text-sm">
                          <p className="font-semibold text-[#171717] mb-1">{label}</p>
                          <p className="text-emerald-600">{fmt(payload[0]?.value)} secured</p>
                          <p className="text-red-500">{fmt(payload[1]?.value)} unsecured</p>
                        </div>
                      );
                    }} />
                    <Area type="monotone" dataKey="securedRevenue" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Secured" />
                    <Area type="monotone" dataKey="unsecuredRevenue" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.15} name="Unsecured" />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', color: '#737373' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Secured vs Unsecured Over Time — Plan Count */}
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <div className="px-6 py-4 border-b border-[#E5E5E5]">
              <h2 className="text-base font-semibold text-[#171717] font-heading">Plan Count Over Time</h2>
              <p className="text-xs text-[#737373]">Number of secured vs unsecured plans as contracts expire</p>
            </div>
            <div className="p-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={securityOverTime} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#737373' }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-3 shadow-lg text-sm">
                          <p className="font-semibold text-[#171717] mb-1">{label}</p>
                          <p className="text-emerald-600">{payload[0]?.value} secured plans</p>
                          <p className="text-red-500">{payload[1]?.value} unsecured plans</p>
                        </div>
                      );
                    }} />
                    <Area type="monotone" dataKey="securedPlans" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Secured" />
                    <Area type="monotone" dataKey="unsecuredPlans" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.15} name="Unsecured" />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', color: '#737373' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quarterly Renewal Timeline Chart */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
              <Calendar className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">Quarterly Renewal Pipeline</h2>
              <p className="text-xs text-[#737373]">Revenue up for renewal by FY quarter (next 5 years)</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarterlyTimeline} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#737373' }} angle={-45} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]} barSize={24}>
                  {quarterlyTimeline.map((entry) => (
                    <Cell key={entry.label} fill={entry.fy === summary.currentFY ? '#171717' : entry.fy === summary.currentFY + 1 ? '#525252' : '#A3A3A3'} opacity={entry.count > 0 ? 0.8 : 0.2} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Manager Breakdown */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
            <Users className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#171717] font-heading">By Manager</h2>
            <p className="text-xs text-[#737373]">Revenue security and upcoming renewals by property manager</p>
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                <th className="px-4 py-2.5 text-left">Manager</th>
                <th className="px-3 py-2.5 text-center">Plans</th>
                <th className="px-3 py-2.5 text-right">Total Revenue</th>
                <th className="px-3 py-2.5 text-right">Secured</th>
                <th className="px-3 py-2.5 text-right">At Risk</th>
                <th className="px-3 py-2.5 text-center">Upcoming Renewals</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {managerBreakdown.map(mgr => (
                <tr key={mgr.manager} className="hover:bg-[#F8F9FA]/50 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-[#171717] text-xs">{mgr.manager}</td>
                  <td className="px-3 py-2.5 text-center text-[#525252] text-xs">{mgr.totalPlans}</td>
                  <td className="px-3 py-2.5 text-right text-xs font-mono text-[#171717]">{fmt(mgr.totalRevenue)}</td>
                  <td className="px-3 py-2.5 text-right text-xs font-mono text-emerald-600">{fmt(mgr.securedRevenue)}</td>
                  <td className="px-3 py-2.5 text-right text-xs font-mono text-red-500">
                    {mgr.unsecuredRevenue > 0 ? fmt(mgr.unsecuredRevenue) : '-'}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {mgr.upcomingRenewals > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        {mgr.upcomingRenewals}
                      </span>
                    ) : (
                      <span className="text-[#A3A3A3] text-xs">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan Detail Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">Plan Detail</h2>
              <p className="text-xs text-[#737373]">All plans sorted by contract expiry date</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
            <input
              data-testid="timeline-plan-search"
              type="text"
              placeholder="Search plans..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-[8px] bg-[#F8F9FA] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] w-56"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                <th className="px-4 py-2.5 text-left">Plan</th>
                <th className="px-4 py-2.5 text-left">Property</th>
                <th className="px-4 py-2.5 text-left">Manager</th>
                <th className="px-4 py-2.5 text-right">Revenue</th>
                <th className="px-4 py-2.5 text-center">Expiry</th>
                <th className="px-4 py-2.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {filteredPlans.slice(0, 100).map(plan => (
                <tr key={plan.planNumber} className="hover:bg-[#F8F9FA]/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-xs font-medium text-[#171717]">{plan.planNumber}</td>
                  <td className="px-4 py-2.5 text-xs text-[#525252] truncate max-w-[150px]">{plan.propertyName || '-'}</td>
                  <td className="px-4 py-2.5 text-xs text-[#525252]">{plan.manager}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-[#171717]">{fmt(plan.revenue)}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-[#525252]">
                    {plan.contractExpiry ? formatDateAU(plan.contractExpiry) : <span className="text-[#A3A3A3]">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {plan.status === 'secured' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Secured
                      </span>
                    ) : plan.status === 'unsecured' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Unsecured
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F5F5F5] text-[#737373] border border-[#E5E5E5]">
                        No Date
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-[#E5E5E5] text-[10px] font-medium text-center uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA] rounded-b-[10px]">
          Showing {Math.min(filteredPlans.length, 100)} of {filteredPlans.length} plans
        </div>
      </div>
    </div>
  );
};

export default RevenueTimeline;
